import { useEffect, useState } from 'react'
import LineSeries from '@/components/charts/LineSeries'
import SleepDriftChart from '@/components/SleepDriftChart'
import { dailySeries, type DailyPoint } from '@/db/queries'
import clsx from 'clsx'

type Range = '7d' | '30d' | '90d' | '1y' | 'all'

const RANGES: { id: Range; label: string; days: number }[] = [
  { id: '7d', label: '7D', days: 7 },
  { id: '30d', label: '30D', days: 30 },
  { id: '90d', label: '90D', days: 90 },
  { id: '1y', label: '1Y', days: 365 },
  { id: 'all', label: 'All', days: 365 * 5 },
]

export default function Trends() {
  const [range, setRange] = useState<Range>('30d')
  const [data, setData] = useState<DailyPoint[]>([])

  useEffect(() => {
    const days = RANGES.find((r) => r.id === range)?.days ?? 30
    dailySeries(days).then(setData)
  }, [range])

  const display = data.length > 90 ? rollup(data, 7) : data

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Trends</h1>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              className={clsx(
                'px-3 py-1 text-sm rounded',
                range === r.id
                  ? 'bg-brand/25 text-brand-fg font-medium'
                  : 'text-stone-600 hover:bg-stone-100',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <Section title="Recovery & HRV">
        <LineSeries
          data={display as unknown as Record<string, unknown>[]}
          xKey="day"
          series={[
            { key: 'recovery', label: 'Recovery %', color: '#16a34a' },
            { key: 'hrv', label: 'HRV (ms)', color: '#a855f7' },
          ]}
        />
      </Section>

      <Section title="Strain">
        <LineSeries
          data={display as unknown as Record<string, unknown>[]}
          xKey="day"
          series={[{ key: 'strain', label: 'Strain', color: '#0ea5e9' }]}
        />
      </Section>

      <Section title="Sleep performance">
        <LineSeries
          data={display as unknown as Record<string, unknown>[]}
          xKey="day"
          series={[
            { key: 'sleep_performance', label: 'Sleep performance %', color: '#a855f7' },
          ]}
        />
      </Section>

      <Section title="Sleep schedule drift">
        <p className="text-xs text-stone-500 mb-3">
          When did your bedtime and wake time drift over this window?
        </p>
        <SleepDriftChart
          daysBack={RANGES.find((r) => r.id === range)?.days ?? 30}
        />
      </Section>

      <p className="text-xs text-stone-500">
        Showing {display.length} {data.length > 90 ? 'weekly buckets' : 'daily points'} ·{' '}
        {data.length} raw days
      </p>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="text-xs uppercase tracking-wider text-stone-500 mb-3">{title}</div>
      {children}
    </div>
  )
}

/**
 * Roll up daily points into weekly averages so multi-year charts stay readable.
 */
function rollup(data: DailyPoint[], windowDays: number): DailyPoint[] {
  const out: DailyPoint[] = []
  for (let i = 0; i < data.length; i += windowDays) {
    const slice = data.slice(i, i + windowDays)
    const avg = (key: keyof DailyPoint): number | undefined => {
      const xs = slice.map((s) => s[key]).filter((x): x is number => typeof x === 'number')
      return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : undefined
    }
    out.push({
      day: slice[0].day,
      recovery: avg('recovery'),
      hrv: avg('hrv'),
      rhr: avg('rhr'),
      strain: avg('strain'),
      sleep_performance: avg('sleep_performance'),
      sleep_minutes: avg('sleep_minutes'),
    })
  }
  return out
}
